<script lang="ts">
  import { onMount } from "svelte";
  import {
    HUBSPOT_AUTH_CHANGED_EVENT,
    beginHubspotOAuthLogin,
    ensureHubspotAuthenticated,
    extractApiMessage,
    fetchJsonWithHubspotAuth,
    initializeHubspotOpportunityFields,
    isHubspotAuthRequiredError,
    logoutHubspotSession,
    openHubspotAuthDialog,
    refreshHubspotSessionWithToken,
  } from "../lib/hubspotAuth";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";

  const baseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;

  // --- State ---
  let syncing = false;
  let syncMessage = "";
  let syncResults: any[] = [];
  let awsSyncResult: any = null;
  let awsSyncError = "";
  let awsSyncDirection = "";

  function dismissAwsResult() {
    awsSyncResult = null;
    awsSyncError = "";
  }

  let isAuthenticated = false;
  let authMessage = "";
  let loading = false;
  let showRefreshInput = false;
  let refreshTokenInput = "";

  // --- HubSpot Auth ---
  const checkAuth = async () => {
    isAuthenticated = await ensureHubspotAuthenticated();
    authMessage = isAuthenticated
      ? "You are logged in to HubSpot."
      : "You are not logged in to HubSpot.";
    
    if (isAuthenticated) {
      await initHubspotV2();
    }
    
  };

  const loginWithHubSpot = async () => {
    try {
      await beginHubspotOAuthLogin();
    } catch {
      authMessage = "Could not fetch HubSpot login URL.";
    }
  };

  const logout = async () => {
    const result = await logoutHubspotSession();
    refreshTokenInput = "";
    showRefreshInput = false;
    await checkAuth();
    authMessage = result.message;
  };

  const submitRefreshToken = async () => {
    loading = true;
    try {
      const result = await refreshHubspotSessionWithToken(refreshTokenInput);
      if (result.success) {
        authMessage = "Signed in successfully with refresh token.";
        await checkAuth();
      } else {
        authMessage = result.message || "Invalid refresh token.";
      }
    } catch {
      authMessage = "Error validating refresh token.";
    } finally {
      loading = false;
    }
  };

  // --- AWS Sync ---
  const syncFromAWS = async () => {
    syncing = true;
    awsSyncResult = null;
    awsSyncError = "";
    awsSyncDirection = "from";
    try {
      const res = await fetch(`${baseUrl}/alace/sync/from-aws`, {
        method: "GET",
      });
      const data = await res.json();
      awsSyncResult = { success: res.ok, ...data };
      if (res.ok && data?.success !== false) {
        triggerOpportunityDataRefresh();
      }
    } catch (err: any) {
      awsSyncError = `Failed to sync from AWS: ${err?.message || err}`;
    } finally {
      syncing = false;
    }
  };

  const syncToAWS = async () => {
    syncing = true;
    awsSyncResult = null;
    awsSyncError = "";
    awsSyncDirection = "to";
    try {
      const res = await fetch(`${baseUrl}/alace/sync/to-aws`, {
        method: "GET",
      });
      const data = await res.json();
      awsSyncResult = data;
      if (res.ok && data?.success !== false) {
        triggerOpportunityDataRefresh();
      }
    } catch {
      awsSyncError = "Failed to sync to AWS.";
    } finally {
      syncing = false;
    }
  };

  const syncToHubspot = async () => {
    if (!isAuthenticated) {
      syncMessage = "Please login to HubSpot first.";
      openHubspotAuthDialog("HubSpot login is required before syncing opportunities to HubSpot.");
      return;
    }
    syncing = true;
    syncMessage = "";
    syncResults = [];
    try {
      const initialized = await initHubspotV2();
      if (!initialized) {
        syncMessage = "HubSpot V2 field init failed. Please retry.";
        return;
      }

      const { response, data, text } = await fetchJsonWithHubspotAuth(
        `${baseUrl}/hubspot-sync/to-hubspot`,
        {
          method: "POST",
        },
        {
          authRequiredMessage: "HubSpot login is required before syncing opportunities to HubSpot.",
        }
      );
      const result = data ?? {};
      if (!response.ok) {
        syncMessage = extractApiMessage(result, text, "Failed to sync to HubSpot.");
        return;
      }
      syncMessage = result.success ? "" : result.error;
      syncResults = result.results ?? [];
      if (result.success !== false && !result.error) {
        triggerOpportunityDataRefresh();
      }
    } catch (err: unknown) {
      if (!isHubspotAuthRequiredError(err)) {
        syncMessage = err instanceof Error ? err.message : "Failed to sync to HubSpot.";
      }
    } finally {
      syncing = false;
    }
  };

  const syncFromHubspot = async () => {
    if (!isAuthenticated) {
      syncMessage = "Please login to HubSpot first.";
      openHubspotAuthDialog("HubSpot login is required before syncing opportunities from HubSpot.");
      return;
    }
    syncing = true;
    syncMessage = "";
    syncResults = [];
    try {
      const initialized = await initHubspotV2();
      if (!initialized) {
        syncMessage = "HubSpot V2 field init failed. Please retry.";
        return;
      }

      const { response, data, text } = await fetchJsonWithHubspotAuth(
        `${baseUrl}/hubspot-sync/from-hubspot`,
        {
          method: "POST",
        },
        {
          authRequiredMessage: "HubSpot login is required before syncing opportunities from HubSpot.",
        }
      );
      const result = data ?? {};
      if (!response.ok) {
        syncMessage = extractApiMessage(result, text, "Failed to sync from HubSpot.");
        return;
      }
      syncMessage = result.success
        ? `Sync from HubSpot completed. Created=${result.created}, Updated=${result.updated}, Skipped=${result.skipped}, Errors=${result.errors}`
        : result.error;
      syncResults = result.results ?? [];
      if (result.success !== false && !result.error) {
        triggerOpportunityDataRefresh();
      }
    } catch (err: unknown) {
      if (!isHubspotAuthRequiredError(err)) {
        syncMessage = err instanceof Error ? err.message : "Failed to sync from HubSpot.";
      }
    } finally {
      syncing = false;
    }
  };

  const initHubspotV2 = async (): Promise<boolean> => {
    const result = await initializeHubspotOpportunityFields();
    if (!result.success && !result.authRequired) {
      console.error("Failed to initialize HubSpot V2 fields: ", result.message);
    }
    return result.success;
  };

  onMount(() => {
    const handleAuthChanged = () => {
      void checkAuth();
    };

    const storedToken = localStorage.getItem("hubspotRefreshToken");
    if (storedToken) {
      refreshTokenInput = storedToken;
    }

    window.addEventListener(HUBSPOT_AUTH_CHANGED_EVENT, handleAuthChanged);

    void (async () => {
      await checkAuth();
    })();

    return () => {
      window.removeEventListener(HUBSPOT_AUTH_CHANGED_EVENT, handleAuthChanged);
    };
  });
</script>

<!-- UI -->
<div class="space-y-6">
  <!-- HubSpot Auth Section -->
  <div class="sync-auth-legacy p-4 bg-[#1e2233] rounded shadow">
    <h2 class="font-bold text-secondary-300">HubSpot Authentication</h2>
    <p class="text-sm text-secondary-400">{authMessage}</p>

    {#if isAuthenticated}
      <div class="flex gap-3 mt-3">
        <button
          on:click={logout}
          class="sync-auth-logout px-4 py-2 bg-red-500 text-white rounded shadow hover:bg-red-600"
        >
          Logout
        </button>
      </div>
    {:else}
      <div class="flex flex-col gap-3 mt-3">
        <button
          on:click={loginWithHubSpot}
          class="sync-auth-primary px-4 py-2 bg-orange-500 text-white rounded shadow hover:bg-orange-600"
        >
          Sign in with HubSpot
        </button>

        <button
          on:click={() => (showRefreshInput = !showRefreshInput)}
          class="sync-auth-link text-sm text-orange-600 underline"
        >
          {showRefreshInput ? "Hide refresh token option" : "Or use a refresh token"}
        </button>

        {#if showRefreshInput}
          <div class="sync-auth-refresh flex gap-2 items-center">
            <input
              type="text"
              placeholder="Enter refresh token"
              bind:value={refreshTokenInput}
              class="sync-auth-input flex-1 border rounded px-2 py-1 text-sm"
            />
            <button
              on:click={submitRefreshToken}
              disabled={loading || !refreshTokenInput}
              class="sync-auth-submit px-3 py-1 bg-orange-500 text-white rounded shadow disabled:opacity-50"
            >
              {loading ? "Signing in..." : "Submit"}
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Sync Section -->
  <div class="p-4 bg-[#24283a] rounded shadow space-y-4">
    <h2 class="font-bold text-secondary-300">Opportunity Sync</h2>

    <!-- AWS Sync -->
    <div class="flex gap-4">
      <button
        on:click={syncFromAWS}
        class="px-4 py-2 text-sm font-semibold bg-primary-600 text-white rounded-lg shadow hover:bg-primary-700 transition disabled:opacity-50"
        disabled={syncing}
      >
        {syncing ? "Syncing..." : "⬇ Sync from AWS"}
      </button>
      <button
        on:click={syncToAWS}
        class="btn-chip px-4 py-2 text-sm font-semibold rounded-lg transition disabled:opacity-50"
        disabled={syncing}
      >
        {syncing ? "Syncing..." : "⬆ Sync to AWS"}
      </button>
    </div>

    <!-- HubSpot Sync (only if logged in) -->
    {#if isAuthenticated}
      <div class="flex gap-4">
        <button
          on:click={syncToHubspot}
          class="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition disabled:opacity-50"
          disabled={syncing}
        >
          {syncing ? "Syncing..." : "⬆ Sync to HubSpot"}
        </button>
        <button
          on:click={syncFromHubspot}
          class="px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition disabled:opacity-50"
          disabled={syncing}
        >
          {syncing ? "Syncing..." : "⬇ Sync from HubSpot"}
        </button>
      </div>
    {/if}

    {#if awsSyncError}
      <div class="text-sm text-red-800 bg-red-50 border border-red-200 p-3 rounded shadow">
        {awsSyncError}
        <button on:click={dismissAwsResult} class="ml-2 text-red-500 hover:text-red-700 font-bold">&times;</button>
      </div>
    {/if}

    {#if awsSyncResult}
      <div class="text-sm bg-[#24283a] border border-[#2f3449] rounded-lg shadow overflow-hidden">
        <div class="flex items-center justify-between px-4 py-3 {awsSyncResult.success ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'}">
          <span class="font-semibold {awsSyncResult.success ? 'text-green-800' : 'text-red-800'}">
            {awsSyncResult.success ? '✓' : '✗'} Sync {awsSyncDirection} AWS {awsSyncResult.success ? 'completed' : 'completed with errors'}
          </span>
          <div class="flex items-center gap-3 text-xs">
            {#if awsSyncResult.synced?.length}
              <span class="bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">{awsSyncResult.synced.length} synced</span>
            {/if}
            {#if awsSyncResult.failed?.length}
              <span class="bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">{awsSyncResult.failed.length} failed</span>
            {/if}
            {#if awsSyncResult.skipped?.length}
              <span class="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">{awsSyncResult.skipped.length} skipped</span>
            {/if}
            <button on:click={dismissAwsResult} class="text-secondary-500 hover:text-secondary-400 font-bold text-lg leading-none">&times;</button>
          </div>
        </div>
        <div class="max-h-64 overflow-y-auto">
          {#if awsSyncResult.failed?.length}
            <div class="px-4 py-2 bg-red-50">
              <div class="font-medium text-red-700 mb-1">Failed ({awsSyncResult.failed.length})</div>
              {#each awsSyncResult.failed as id}
                <div class="mb-2 p-2 bg-[#24283a] border border-red-200 rounded text-xs">
                  <div class="font-medium text-secondary-300">Item #{id}</div>
                  {#if awsSyncResult.responses?.[id]}
                    <div class="text-red-600 mt-1">
                      {#if awsSyncResult.responses[id].Reason}
                        <span class="font-medium">{awsSyncResult.responses[id].Reason}</span>
                      {/if}
                      {#if awsSyncResult.responses[id].ErrorList}
                        {#each awsSyncResult.responses[id].ErrorList as error}
                          <div class="mt-0.5 text-red-500">
                            <span class="font-medium">{error.FieldName}:</span> {error.Message}
                          </div>
                        {/each}
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
          {#if awsSyncResult.synced?.length}
            <div class="px-4 py-2">
              <div class="font-medium text-green-700 mb-1">Synced ({awsSyncResult.synced.length})</div>
              <div class="grid grid-cols-2 gap-1">
                {#each awsSyncResult.synced as id}
                  <div class="text-xs text-secondary-400 p-1 bg-green-50 rounded flex justify-between">
                    <span>#{id}</span>
                    {#if awsSyncResult.responses?.[id]?.Id}
                      <span class="text-secondary-500">{awsSyncResult.responses[id].Id}</span>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/if}
          {#if awsSyncResult.skipped?.length}
            <div class="px-4 py-2 bg-yellow-50">
              <div class="font-medium text-yellow-700 mb-1">Skipped ({awsSyncResult.skipped.length})</div>
              <div class="flex flex-wrap gap-1">
                {#each awsSyncResult.skipped as id}
                  <span class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">#{id}</span>
                {/each}
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}

    {#if syncMessage}
      <div class="text-sm text-secondary-200 bg-[#2c3145] p-3 rounded shadow">
        {syncMessage}
      </div>
    {/if}

    {#if syncResults.length > 0}
      <div class="mt-4 max-h-48 overflow-auto overflow-x-hidden pr-1 max-w-full">
        <h3 class="font-bold text-secondary-200">Details:</h3>
        <ul class="text-sm space-y-1 break-words whitespace-pre-wrap w-full text-secondary-300">
          {#each syncResults as r}
            <li class="break-words break-all">
              HubSpot ID: {r.hubspotId || "-"} | CRM ID: {r.crmId} | AWS ID: {r.awsId || "-"} |
              {r.status ? `${r.status}` : `${r.error}`}
            </li>
          {/each}
        </ul>
      </div>
    {/if}
  </div>
</div>

<style>
  .btn-chip {
    background: #303650;
    border: 1px solid rgba(226, 232, 240, 0.22);
    color: #e8ecf6;
  }

  .btn-chip:hover:not(:disabled) {
    background: #3b415c;
    color: #ffffff;
  }

  .sync-auth-legacy {
    border: 1px solid rgba(226, 232, 240, 0.14);
    background: #24283a;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .sync-auth-primary,
  .sync-auth-submit,
  .sync-auth-logout {
    border: 0;
    cursor: pointer;
    transition:
      background-color 150ms ease,
      box-shadow 150ms ease,
      opacity 150ms ease;
  }

  .sync-auth-primary,
  .sync-auth-submit {
    background: #f97316;
    color: #ffffff;
  }

  .sync-auth-primary:hover:enabled,
  .sync-auth-submit:hover:enabled {
    background: #ea580c;
  }

  .sync-auth-logout {
    background: #ef4444;
    color: #ffffff;
  }

  .sync-auth-logout:hover:enabled {
    background: #dc2626;
  }

  .sync-auth-link {
    background: transparent;
    border: 0;
    cursor: pointer;
    padding: 0;
  }

  .sync-auth-refresh {
    max-width: 36rem;
  }

  .sync-auth-input {
    background: #ffffff;
    border-color: #d1d5db;
    color: #1f2937;
    outline: none;
  }

  .sync-auth-input:focus {
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
  }
</style>
